import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { useTranslation } from "@/utils/hooks/useTranslation";

export function KeyboardShortcutsDialog() {
  const { t } = useTranslation('shortcuts');
  const [open, setOpen] = useState(false);

  const shortcuts = [
    { category: t('general'), items: [
      { keys: "Ctrl + S", description: t('saveDocument') },
      { keys: "Ctrl + Shift + P", description: t('togglePreviewSplitMode') },
      { keys: "Ctrl + /", description: t('showKeyboardShortcuts') },
      { keys: "Escape", description: t('closePanelOrDialog') },
    ]},
    { category: t('textFormatting'), items: [
      { keys: "Ctrl + B", description: t('bold') },
      { keys: "Ctrl + I", description: t('italic') },
      { keys: "Ctrl + U", description: t('underline') },
      { keys: "Ctrl + Z", description: t('undo') },
      { keys: "Ctrl + Y", description: t('redo') },
    ]},
    { category: t('blocks'), items: [
      { keys: "/", description: t('openSlashCommandMenu') },
      { keys: "Enter", description: t('newParagraphAfterCurrentBlock') },
      { keys: "Backspace", description: t('deleteEmptyBlock') },
      { keys: "Ctrl + Alt + 1–4", description: t('headingLevel') },
      { keys: "Ctrl + Shift + V", description: t('pastePlainText') },
      { keys: "Ctrl + Shift + G", description: t('grammarCheck') },
    ]},
  ];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {shortcuts.map((section) => (
            <div key={section.category} className="mb-4 last:mb-0">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)] m-0 mb-2">
                {section.category}
              </h4>
              {section.items.map((item) => (
                <div
                  key={item.keys}
                  className="flex items-center justify-between py-1.5 [&:not(:last-child)]:border-b border-[var(--lilia-border)]"
                >
                  <kbd className="font-mono text-xs px-2 py-0.5 bg-[var(--lilia-bg)] border border-[var(--lilia-border)] rounded text-[var(--lilia-text)] whitespace-nowrap">
                    {item.keys}
                  </kbd>
                  <span className="text-[13px] text-[var(--lilia-text)]">{item.description}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t('common:close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
